/**
 * Tipi condivisi per il modulo Agenda.
 *
 * Unione di tutti i campi usati in:
 *   AgendaPageNew, AgendaContent, CalendarMonthView,
 *   CalendarWeekView, CalendarDayView, NewEventModal, EventDetailModal
 */

export type EventType = 'meeting' | 'evento' | 'attivita' | 'task' | 'scadenza'

export type DeadlineStatus = 'TODO' | 'COMPLETED' | 'CANCELLED'

export const DEADLINE_STATUS_LABELS: Record<DeadlineStatus, string> = {
  TODO: 'Da fare',
  COMPLETED: 'Completata',
  CANCELLED: 'Annullata',
}

export type AppointmentType = 'MEETING' | 'EVENT' | 'APPOINTMENT' | 'DEADLINE'

/** API date-only strings (YYYY-MM-DD) must not be converted to UTC datetimes. */
interface AppointmentResponseBase {
  id: number
  title: string
  date: string
  description?: string | null
  location?: string | null
  participants?: number[]
  client?: number | null
  is_past: boolean
  is_overdue: boolean
}

export type AppointmentResponse = AppointmentResponseBase & (
  | {
      appointment_type: 'DEADLINE'
      start_time: null
      end_time: null
      duration: null
      is_all_day: true
      deadline_status: DeadlineStatus
    }
  | {
      appointment_type: Exclude<AppointmentType, 'DEADLINE'>
      start_time: string
      end_time: string
      duration: string
      is_all_day: false
      deadline_status: null
    }
)

export interface CalendarEvent {
  id: number
  title: string
  start_time: string | null
  end_time?: string | null
  is_all_day?: boolean
  deadline_status?: DeadlineStatus | null
  location?: string | null
  type: EventType
  /** Campo backend grezzo — presente nelle risposte API */
  appointment_type?: string
  start_date: string
  end_date?: string
  description?: string | null
  /** Stringa CSV legacy oppure array di oggetti dal backend */
  participants?: string | any[]
  send_reminder?: boolean
  is_recurring?: boolean
  add_video_link?: boolean
  /** Origine dell'evento: appointment API o task API */
  source?: 'appointment' | 'task'
  /** Data di scadenza (solo per task) */
  due_date?: string
  /** Task o scadenza da fare con data passata */
  is_overdue?: boolean
  /** Task completato (solo per task) */
  is_completed?: boolean
  /** Priorità task (solo per task): 'ALTA' | 'MEDIA' | 'BASSA' */
  priority?: string
}

export type ParticipantType = 'team' | 'contact'

export interface Participant {
  id: string
  name: string
  type: ParticipantType
}

export interface FilterState {
  eventType: string[]
  participation: string[]
}

export type ViewMode = 'Month' | 'Week' | 'Day'

/**
 * Mapping backend appointment_type → frontend EventType.
 * Usato in EventDetailModal e nella mappatura risposte API.
 */
export const APPOINTMENT_TYPE_MAP: Record<string, EventType> = {
  MEETING: 'meeting',
  EVENT: 'evento',
  APPOINTMENT: 'attivita',
  DEADLINE: 'scadenza',
}

export const EVENT_TO_APPOINTMENT_TYPE: Partial<Record<EventType, AppointmentType>> = {
  meeting: 'MEETING',
  evento: 'EVENT',
  attivita: 'APPOINTMENT',
  scadenza: 'DEADLINE',
}
